import { toast as hotToast, type Toast, type ToastOptions } from 'react-hot-toast'
import { EmporionToastCard, type EmporionToastVariant } from '../../components/toast/EmporionToastCard'

type EmporionToastInput =
  | string
  | {
    title: string
    description?: string
  }

type EmporionToastOptions = ToastOptions

const resolvePayload = (
  input: EmporionToastInput,
  fallbackTitle: string,
): {
  title: string
  description?: string
} => {
  if (typeof input === 'string') {
    return { title: input || fallbackTitle }
  }
  return {
    title: input.title || fallbackTitle,
    description: input.description,
  }
}

const durationByVariant: Record<EmporionToastVariant, number> = {
  success: 3600,
  error: 5200,
  warning: 4600,
  info: 3600,
  loading: Infinity,
}

const renderToastCard = (
  variant: EmporionToastVariant,
  input: EmporionToastInput,
  options?: EmporionToastOptions,
) => {
  const payload = resolvePayload(input, 'Notification')
  return hotToast.custom(
    (t: Toast) => (
      <EmporionToastCard
        show={t.visible}
        variant={variant}
        title={payload.title}
        description={payload.description}
        onClose={() => hotToast.dismiss(t.id)}
      />
    ),
    {
      duration: durationByVariant[variant],
      ...options,
    },
  )
}

type EmporionToastFn = {
  (input: EmporionToastInput, options?: EmporionToastOptions): string
  success: (input: EmporionToastInput, options?: EmporionToastOptions) => string
  error: (input: EmporionToastInput, options?: EmporionToastOptions) => string
  warning: (input: EmporionToastInput, options?: EmporionToastOptions) => string
  info: (input: EmporionToastInput, options?: EmporionToastOptions) => string
  loading: (input: EmporionToastInput, options?: EmporionToastOptions) => string
  dismiss: (toastId?: string) => void
  remove: (toastId?: string) => void
}

const defaultToast = ((input: EmporionToastInput, options?: EmporionToastOptions) =>
  renderToastCard('info', input, options)) as EmporionToastFn

defaultToast.success = (input, options) => renderToastCard('success', input, options)
defaultToast.error = (input, options) => renderToastCard('error', input, options)
defaultToast.warning = (input, options) => renderToastCard('warning', input, options)
defaultToast.info = (input, options) => renderToastCard('info', input, options)
defaultToast.loading = (input, options) => renderToastCard('loading', input, options)
defaultToast.dismiss = (toastId?: string) => hotToast.dismiss(toastId)
defaultToast.remove = (toastId?: string) => hotToast.remove(toastId)

export const emporionToast = defaultToast
